import { Country } from './country-types'
import { SdgCompilance } from './sdg-compliances.interfaces'
import moment from 'moment'

export type ProjectKindType =
    | 'compensation'
    | 'energy'
    | 'contribution'
    | 'biodiversity'
    | 'carbon_forward'

export type FulfillmentType = 'cancel' | 'transfer' | 'hold'

export interface ProjectSliceType {
    createProject: CreateProjectBrokerage
    validateProjectId: SmallRequest
    validateProjectIdForStockSale: SmallRequest
    createPurchaseOperation: Request
    createBrokerageOperation: Request
    userProjects: UserProjectsState
    projectDetails: ProjectDetailsState
    transferProject: Request
    createBigProject: Request
    editBigProject: Request
    projectForm: ProjectFormState
}

export type ProjectFormStepsType =
    | 'DEFINITION'
    | 'LOCATION'
    | 'CONTENT'
    | 'STOCK'
    | 'CERTIFICATION'
    | 'DOCUMENTATION'
    | 'CONFIGURATION'
    | 'TAX_RELIEF'
export type ContributionProjectType = 'CONTRIBUTION_UNIT' | 'FREE_DONATION'
export type PosibleLocale =
    | 'en'
    | 'es'
    | 'fr'
    | 'ko'
    | 'de'
    | 'it'
    | 'pt'
    | 'pl'
    | 'zh'
export type ProjectStageType =
    | 'waiting_verification'
    | 'in_validation'
    | 'pilot_phase'

export interface ProjectFormState {
    activeStep: ProjectFormStepsType
    kind: ProjectKindType
    is_active: boolean
    latitude: string
    longitude: string
    country: number
    locationText: string
    name: string
    url: string
    sdg_compliances: number[]
    description_en: string
    description_es: string
    description_fr: string
    description_ko: string
    description_pt: string
    description_de: string
    description_it: string
    description_pl: string
    description_zh: string
    registered_since: string
    video: string
    thumbnail: string
    cover: string
    main_image: string
    pictures: string[]
    contributionProjectType: ContributionProjectType
    contribution_unit: string
    price: number
    currency_token: number
    volume_credits: number
    is_vintage: boolean
    vintage_items: VintageItemPayload[]
    currentVintage: VintageItemPayload
    is_tax_relief: boolean
    tax_info_en: string
    tax_info_es: string
    tax_info_fr: string
    tax_info_ko: string
    tax_info_pt: string
    tax_info_de: string
    tax_info_it: string
    tax_info_pl: string
    tax_info_zh: string
    status: string
    type: string
    sub_type: string
    methodology: string
    energy_type: string
    central_name: string
    standard_energy_type: string
    validator: string
    first_verifier: string
    standard: string
    stage: ProjectStageType
    credit_start: string
    credit_end: string
    mechanism: string
    quantification_methodology: string
    complementary_standards: string
    registry_depositing_id: string
    registry_depositing_name: string
    registry_depositing_url: string
    credits_certificate: string
    pdd: string
    validation_report: string
    monitoring_report: string
    verification_report: string
    is_property_of_climatetrade: boolean
    billing_company_name: string
    billing_vat_number: string
    billing_address: string
    billing_city: string
    billing_state: string
    billing_postal_code: string
    billing_country: number
    device_registration_request: string
    resulting_from_the_issuer: string
    start_up_date_of_installation: string
    headline_en: string
    headline_es: string
    headline_pt: string
    headline_de: string
    headline_ko: string
    headline_it: string
    headline_fr: string
    headline_pl: string
    headline_zh: string
    discounts: ProjectDiscountPayload[]
    currentDiscount: ProjectDiscountPayload
    type_of_biome_ecosystem: string
    protocol_implementation_scheme: string
    executive_summary: string
    additional_documentation: string
    insurance_documentation: string
}

export interface VintageItemPayload {
    id?: number
    year: number
    stock: number
    price: number
    integration_registry_vintage_id?: string
    delivery_date?: moment.Moment | string
}

// This is managed like this on backend
export const LOGO_STANDARDS = {
  ACR: 'ACR Standard',
  ARTTREES: 'ART-TREES',
  BCR: 'BCR Standard',
  CAR: 'CAR Standard',
  CDM: 'CDM Standard',
  CERCARBONO: 'CerCarbono',
  GCC: 'GCC Standard',
  GOLD: 'Gold Standard',
  MITECO: 'MITECO',
  VCS: 'VCS',
  UKWOODLAND: 'UK Woodland Carbon Code',
  PURO: 'Puro Standard',
  PLANVIVO: 'Plan Vivo Standard',
  IREC: 'IREC Standard',
  LCL: 'Low Carbon Label (LCL)',
  ICR: 'ICR Standard'
} as const

export type ProjectStandardType =
    | typeof LOGO_STANDARDS.ACR
    | typeof LOGO_STANDARDS.ARTTREES
    | typeof LOGO_STANDARDS.BCR
    | typeof LOGO_STANDARDS.CAR
    | typeof LOGO_STANDARDS.CDM
    | typeof LOGO_STANDARDS.CERCARBONO
    | typeof LOGO_STANDARDS.GCC
    | typeof LOGO_STANDARDS.GOLD
    | typeof LOGO_STANDARDS.MITECO
    | typeof LOGO_STANDARDS.VCS
    | typeof LOGO_STANDARDS.UKWOODLAND
    | typeof LOGO_STANDARDS.PURO
    | typeof LOGO_STANDARDS.PLANVIVO
    | typeof LOGO_STANDARDS.IREC
    | typeof LOGO_STANDARDS.LCL
    | typeof LOGO_STANDARDS.ICR

interface ProjectDetailsState {
    loading: boolean
    project: Project
}

interface CreateProjectBrokerage {
    loading: boolean
    success: boolean
    response: {
        id: number
        volume_credits: number
    }
}

interface SmallRequest {
    loading: boolean
    response: any
    project?: Project
}

interface Request {
    loading: boolean
    success?: boolean
    newProjectId?: number
    error: any
    newPurchaseLink?: string
}

interface UserProjectsState {
    loading: boolean
    data: Project[]
    filterText: string
    filterType: FilterProjectType
}

export type FilterProjectType = 'ALL' | 'VINTAGE' | 'NO_VINTAGE'
export type MeasurementType = 'tCO2e' | 'MWh' | 'UD' | 'CR' | 'tCO₂'

export interface Project {
    id: number
    name: string
    kind: ProjectKindType
    description: string
    code: string
    is_vintage: boolean
    get_vintage_items_sum_stock: number
    get_vintage_items_available_sum_stock: number
    vintage_items: VintageItem[]
    url: string
    country: Country
    region: string
    thumbnail: string
    cover: string
    main_image: string
    sdg_compliances: SdgCompilance[]
    company: number
    street: string
    city: string
    state: string
    postal_code: string
    latitude: string
    longitude: string
    status: string
    type: string
    sub_type: string
    methodology: string
    credit_start: Date | string
    credit_end: Date | string
    validator: string
    stage: ProjectStageType
    first_verifier: string
    standard: ProjectStandardType | string
    volume_credits: number
    compensated_credits: number
    available_volume_credits: number
    co2_balance: number
    price: number
    kgco2_minimum_purchase: number
    mechanism: string
    currency_token: number
    token: number
    original_project: number
    vat_percentage: number
    registry_depositing_id: string
    registry_depositing_name: string
    registry_depositing_url: string
    credits_certificate: string
    pdd: string
    validation_report: string
    monitoring_report: string
    verification_report: string
    integration_registry: string
    integration_registry_project_id: string
    pictures: ProjectImage[]
    created: Date
    is_active: boolean
    is_upcoming: boolean
    is_featured: boolean
    bc_address: string
    recent_purchases: RecentPurchase[]
    is_visible_in_climatetrade: boolean
    is_property_of_climatetrade: boolean
    registered_since: Date | string
    video: string
    external_fee: number
    billing_company_name: string
    billing_vat_number: string
    billing_address: string
    billing_country: number
    billing_city: string
    billing_state: string
    billing_postal_code: string
    is_compensation_kind: boolean
    is_contribution_kind: boolean
    is_energy_type: boolean
    energy_type: string
    central_name: string
    standard_energy_type: string
    unit_of_measurement: MeasurementType
    has_trans_descriptions: boolean
    descriptions: ProjectDescription[]
    is_free_donation: boolean
    contribution_unit: string
    tags: { id: number; name: string }[]
    is_tax_relief: boolean
    tax_info: string
    tax_info_en: string
    tax_info_es: string
    tax_info_pt: string
    tax_info_de: string
    tax_info_ko: string
    tax_info_it: string
    tax_info_fr: string
    tax_info_zh: string
    final_unit_price: FinalUnitPrice
    referral: string
    has_impacts: boolean
    impacts: ProjectImpact[]
    device_registration_request: string
    resulting_from_the_issuer: string
    start_up_date_of_installation: string
    headline: string
    headline_en: string
    headline_es: string
    headline_pt: string
    headline_de: string
    headline_ko: string
    headline_it: string
    headline_fr: string
    headline_zh: string
    discounts: ProjectDiscountPayload[]
    type_of_biome_ecosystem: string
    protocol_implementation_scheme: string
    executive_summary: string
    additional_documentation: string
    complementary_standards: string
    quantification_methodology: string
    insurance_documentation: string
}

export interface ProjectDescription {
    is_empty: boolean
    language: string
    text: string
}
interface ProjectDiscount {
    type: string
    amount: string
    project: number
    amount_type: string
    min_volume?: number
    date_start?: string | null
    date_end?: string | null
}
interface SupplierDiscounts {
    applied_discounts: ProjectDiscount[]
    applied_discounts_by_amount_type: ProjectDiscount[]
    supplier_discount: number
}
export interface FinalUnitPrice {
    discount_amount: number
    sell_base_amount: number
    fee_amount: number
    fee_amount_percentage: number
    sell_vat: number
    sell_vat_percentage: number
    fee_vat: number
    fee_vat_percentage: number
    total_amount: number
    fixed_amount_transaction_fee: number
    partner_fee_percentage: number
    partner_fee_amount: number
    partner_fee_vat_amount: number
    supplier_discounts: SupplierDiscounts
}

export interface RecentPurchase {
    purchase_id: number
    unit_of_measurement: string
    amount: number
    amount_value: number
    currency: string
    country_code: string
    country_flag: string
    user_type: 'company' | 'individual'
    created: string
}

export interface VintageItem {
    id: number
    year: number
    stock: number
    price: number
    token_asa_id: number
    available_volume_credits: number
    have_available_credits: boolean
    stock_balance: number
    final_unit_price: FinalUnitPrice
    integration_registry_vintage_id: string
    delivery_date: string
}

export interface ProjectImage {
    id: number
    project: string
    image: string
}

export interface ProjectImpact {
    id: number
    name: string
    description: string
    image: string
    icon: string
}

export interface ProjectDescriptionPayload {
    description_en: string
    description_es: string
    description_fr: string
    description_ko: string
    description_pt: string
    description_de: string
    description_it: string
}

export interface ProjectTaxDescriptionsPayload {
    tax_info_en: string
    tax_info_es: string
    tax_info_fr: string
    tax_info_ko: string
    tax_info_pt: string
    tax_info_de: string
    tax_info_it: string
    tax_info_pl: string
    tax_info_zh: string
}

export interface ContentFormPayload {
    name: string
    code: string
    url: string
    sdg_compliances: number[]
    description_en: string
    description_es: string
    description_fr: string
    description_ko: string
    description_pt: string
    description_de: string
    description_it: string
    description_pl: string
    description_zh: string
    registered_since: moment.Moment
    video: string
    thumbnail: File | string
    cover: File | string
    main_image: File | string
    headline_en: string
    headline_es: string
    headline_fr: string
    headline_ko: string
    headline_pt: string
    headline_de: string
    headline_it: string
    headline_pl: string
    headline_zh: string
}

export interface ProjectFormStockPayload {
    currency_token: number
    contribution_unit: string
    price: number
    volume_credits: number
}

export interface ProjectFormCertificationPayload {
    status: string
    type: string
    sub_type: string
    methodology: string
    energy_type: string
    central_name: string
    standard_energy_type: string
    validator: string
    first_verifier: string
    standard: string
    credit_start: moment.Moment
    credit_end: moment.Moment
    mechanism: string
    complementary_standards: string
    quantification_methodology: string
    stage: ProjectStageType
}

export interface ProjectFormDocumentationPayload {
    registry_depositing_id: string
    registry_depositing_name: string
    registry_depositing_url: string
    credits_certificate: string
    pdd: string
    validation_report: string
    monitoring_report: string
    verification_report: string
    device_registration_request: string
    resulting_from_the_issuer: string
    start_up_date_of_installation: string
    protocol_implementation_scheme: string
    executive_summary: string
    additional_documentation: string
    insurance_documentation: string
}

export interface BillingDataAux {
    name: string
    idNumber: string
    address: string
    city: string
    state: string
    postalCode: string
    country: number
}

export interface ProjectContentImages {
    thumbnail: string
    cover: string
    main_image: string
}

export interface ProjectFormDocumentationFiles {
    credits_certificate: string
    pdd: string
    validation_report: string
    monitoring_report: string
    verification_report: string
    device_registration_request: string
    resulting_from_the_issuer: string
    protocol_implementation_scheme: string
    executive_summary: string
    additional_documentation: string
    insurance_documentation: string
}

export interface ProjectApiPayload {
    isEdit: boolean
    name: string
    kind: string
    is_active: boolean
    country: number
    latitude: string
    longitude: string
    description: string
    code: string
    url: string
    sdg_compliances: number[]
    registered_since: string
    video: string
    descriptions: { language: string; text: string }[]
    is_vintage: boolean
    vintage_items: VintageItemPayload[]
    co2_balance: number
    currency_token: number
    price: number
    is_free_donation: boolean
    contribution_unit: string
    status: string
    type: string
    sub_type: string
    methodology: string
    mechanism: string
    complementary_standards: string
    quantification_methodology: string
    energy_type: string
    central_name: string
    standard_energy_type: string
    credit_start: string
    credit_end: string
    start_up_date_of_installation: string
    validator: string
    first_verifier: string
    standard: string
    registry_depositing_id: string
    registry_depositing_name: string
    registry_depositing_url: string
    is_tax_relief: boolean
    tax_info: string
    tax_info_en: string
    tax_info_es: string
    tax_info_fr: string
    tax_info_ko: string
    tax_info_pt: string
    tax_info_de: string
    tax_info_it: string
    tax_info_pl: string
    tax_info_zh: string
    headline: string
    headline_en: string
    headline_es: string
    headline_fr: string
    headline_ko: string
    headline_pt: string
    headline_de: string
    headline_it: string
    headline_pl: string
    headline_zh: string
    is_property_of_climatetrade: boolean
    billing_company_name: string
    billing_vat_number: string
    billing_address: string
    billing_country: number
    billing_city: string
    billing_state: string
    billing_postal_code: string
    pictures?: string[]
    type_of_biome_ecosystem: string
    stage: ProjectStageType
    last_updated: string
}

export interface ProjectFormKeysValue {
    key: keyof ProjectFormState
    value:
        | string
        | number
        | ProjectKindType
        | number[]
        | string[]
        | boolean
        | ProjectFormStepsType
        | VintageItemPayload
        | ProjectStageType
        | VintageItemPayload[]
        | ProjectDiscountPayload
        | ProjectDiscountPayload[]
}

export interface CreateBrokerageProjectPayload {
    name: string
    volume_credits: number
    price: number
    cover: string
    main_image: string
    email?: string
    vintageYear?: number
}

export interface ProjectDiscountPayload {
    id?: string | number
    type: 'FLASH_SALE' | 'VOLUME_DISCOUNT'
    date_start?: string
    date_end?: string
    min_volume?: number
    amount_type: 'PERCENTAGE' | 'FLAT_AMOUNT'
    amount: number
    project?: number
    vintages?: number[]
    vintages_data?: { id: number; year: number }[]
}

export interface ProjectDiscountApiPayload
    extends Omit<ProjectDiscountPayload, 'id'> {}
